import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DsButtonVariants, DsChipColorVariant, DsChipSize, Icons } from '@levelaccess/design-system';

import { TestAutomationTools } from '@shared/constants/test-automation';
import { LevelDocsAmplitude } from '@shared/constants/leveldocs-amplitude.constants';
import { TestAutomationService } from '../../../../../services/test-automation.service';
import { NotificationPosition } from '../../../../../models/notification.model';
import { NotificationService } from '../../../../../services/notification.service';
import { TranslateService } from '../../../../../translate/translate.service';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { DetectUtility } from '../../../../../utility/detect.utility';
import { InstrumentationService } from '../../../../../services/instrumentation.service';
import { ICurrentUserData, UserService } from '../../../../../services/user.service';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import appVersion from '../../../../../../version.json';

export enum DownloadType {
  button,
  dropdown,
  internal_link,
  external_link,
  install,
}

export interface ImageDetails {
  src: string;
  width: number;
}

export interface option {
  label: string;
  tool: TestAutomationTools;
  icon?: Icons;
  action?: (...args: any[]) => any;
}

@Component({
  selector: 'app-tool-template',
  templateUrl: './tool-template.html',
  standalone: false,
  styleUrl: './tool-template.scss',
})
export class ToolTemplateComponent implements OnDestroy {
  @Input()
  public downloadType: DownloadType;
  public DownloadType: typeof DownloadType = DownloadType;
  public DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public Icons: typeof Icons = Icons;
  public readonly DsChipColorVariant: typeof DsChipColorVariant = DsChipColorVariant;
  public readonly DsChipSize: typeof DsChipSize = DsChipSize;

  @Input()
  public image: ImageDetails;

  @Input()
  public dropdownOption?: option[];

  @Input()
  public tool?: TestAutomationTools;

  @Input()
  public label: string;

  @Input()
  public buttonName: string;

  /** Translation key for an optional chip rendered inline with the tool title. */
  @Input()
  public chipLabel?: string;

  @Input()
  public chipColorVariant: DsChipColorVariant = DsChipColorVariant.Primary;

  @Input()
  public buttonVariant: DsButtonVariants = DsButtonVariants.primary;

  @Input()
  public disabled: boolean;

  @Input()
  public url?: string;

  @Output()
  public readonly onClick: EventEmitter<void> = new EventEmitter<void>();

  private subscriptions: Subscription;
  constructor(
    private testAutomationService: TestAutomationService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private errorHandler: LoadErrorHandler,
    private instrumentationService: InstrumentationService,
    private userService: UserService,
  ) {
    this.subscriptions = new Subscription();
  }

  private downloadSuccess(): void {
    this.notificationService.success(
      this.translateService.instant('test_automation_tool_download_success'),
      NotificationPosition.Toast,
    );
  }

  private downloadError(error: unknown): void {
    this.errorHandler.handleError('test_automation_tool_download_error', error as HttpErrorResponse, ToolTemplateComponent.name);
  }

  private openLink(href: string): void {
    location.href = href;
  }

  public handleDropdownClick(dropdownOption: option): void {
    if (SharedCommonUtility.notNullish(dropdownOption.action)) {
      dropdownOption.action();
      return;
    }

    this.download(dropdownOption.tool);
  }

  public download(tool: TestAutomationTools): void {
    this.subscriptions.add(
      this.testAutomationService
        .download(tool)
        .pipe(take(1))
        .subscribe({
          next: (): void => {
            this.downloadSuccess();
            this.trackLevelDocsPluginDownload(tool);
          },
          error: this.downloadError.bind(this),
        }),
    );
  }

  private trackLevelDocsPluginDownload(tool: TestAutomationTools): void {
    if (!this.testAutomationService.isLevelDocsTool(tool)) {
      return;
    }

    const pluginName: string | undefined = LevelDocsAmplitude.getPluginName(tool);
    if (SharedCommonUtility.isNullish(pluginName)) {
      return;
    }

    const { workspaceId, digitalPropertyId }: ICurrentUserData = this.userService.getUserDataForLogging();

    void this.instrumentationService.track(
      LevelDocsAmplitude.INSTRUMENTATION_EVENTS.PLUGIN_DOWNLOADED,
      {
        pluginName,
        platformVersion: appVersion.version,
        eventPagePath: LevelDocsAmplitude.EventPagePath.TOOLS_AND_EXTENSIONS,
      },
      workspaceId,
      digitalPropertyId,
    );
  }

  public install(tool: TestAutomationTools): void {
    if (tool !== TestAutomationTools.extension_firefox || DetectUtility.getBrowserName() !== 'Firefox') {
      return;
    }

    this.subscriptions.add(
      this.testAutomationService
        .getToolUrl(tool)
        .pipe(take(1))
        .subscribe({
          next: this.openLink.bind(this),
          error: this.downloadError.bind(this),
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
